// src/serviceOutage/useReloadOnRecovery.js
//
// Der Konsument des Erholungs-Ereignisses (UI2 Teil 9, Karte Z-1c).
//
// WAS ER TUT
// ----------
// Er haengt eine Rueckruf-Funktion an das Fensterereignis `pog:service-online`
// und ruft sie bei JEDER Erholung genau einmal. Die Flaeche laedt daraufhin ihre
// DATEN neu — die Seite bleibt stehen.
//
// 🔴 KEIN SEITENNEULADEN (Entscheid E-1)
// --------------------------------------
// `window.location.reload()` waere kuerzer und falsch: es gibt in diesem Repo
// keinen Schmutz-Zustandsschutz (`beforeunload` repoweit null Treffer), ein
// Neuladen verwirft also ungesicherten Text. Ein Datenabruf verwirft nichts.
//
// 🔴 WARUM DAS FENSTEREREIGNIS UND NICHT `abonniere()` AUS DEM MODUL
// ------------------------------------------------------------------
// Das Ereignis ist der in `App.js`, `ServiceOutageBanner.jsx` und
// `ServiceOutageProvider.jsx` dokumentierte Vertrag und hat eine eigene Probe
// (`ServiceOutageProvider.test.jsx`). Der Modulzustand traegt dieselbe
// Information eine Schicht tiefer, aber der Uebergang `ausfall -> online` ist
// nur in der React-Schicht beobachtbar (dort steht vorher/nachher), und genau
// deshalb feuert der Provider das Ereignis — ein zweiter Weg waere eine zweite
// Wahrheit.
//
// 🔴 WARUM EIN `useRef` UND KEIN EFFEKT MIT `[reload]`
// ---------------------------------------------------
// `reload` ist bei den meisten Aufrufern NICHT memoisiert (`ManualDriftCard`
// gibt eine frisch erzeugte Funktion je Render). Ein Effekt mit `[reload]` in
// den Abhaengigkeiten haengt den Zuhoerer damit bei JEDEM Render neu — und
// waehrend eines Render-Zyklus ist er kurzzeitig abgemeldet. Der Zuhoerer haengt
// hier deshalb EINMAL (`[]`), und die jeweils JUENGSTE Funktion steht in der
// Referenz. Beides ist gemessen: die Probe „neue `reload`-Referenz" ruft die
// neue Funktion, die Probe „ausgehaengt feuern" ruft keine.

import { useEffect, useRef } from 'react';

/** Das Ereignis, das `ServiceOutageProvider` beim Uebergang `ausfall -> online` feuert. */
export const ERHOLUNGS_EREIGNIS = 'pog:service-online';

/**
 * useReloadOnRecovery — laesst eine Flaeche ihre Daten neu laden, wenn der Dienst zurueckkommt.
 *
 * @description
 *   Nach einem Ausfall bleibt eine Flaeche mit gescheitertem Abruf in ihrem
 *   Fehlerzustand stehen, bis jemand „Reload" oder „Try again" drueckt — das
 *   Banner sagt nur „Back online.". Dieser Hook schliesst die Luecke: er ruft
 *   die uebergebene Ladefunktion bei jeder Erholung genau einmal.
 *
 *   Er ist die React-Bindung des Ereignisses `pog:service-online`, das
 *   `ServiceOutageProvider` beim Uebergang `ausfall -> online` feuert.
 *
 *   🔴 Er laedt NUR DATEN, er laedt die Seite NICHT neu (Entscheid E-1): ein
 *   `location.reload()` verwirft ungesicherten Text, ein Datenabruf nicht.
 *
 *   🔴 Mehrere Erholungen hintereinander (ein flatternder Dienst) laden
 *   mehrfach. Das ist gewollt — jede Erholung ist eine echte Erholung.
 *
 * @useWhen
 *   - Eine Flaeche laedt ihre Daten beim Einhaengen und danach nur auf Klick
 *   - Ihr Ladefehler ist im Ausfall ausgeblendet (E-3) und kaeme sonst nach der Erholung zurueck
 *   - Die Ladefunktion ist stabil ODER je Render neu — beides traegt dieser Hook
 *
 * @dontUseFor
 *   - Ein globales Neuladen der Seite — dafuer gibt es hier keinen Weg, und E-1 will keinen
 *   - Einen laufenden Takt — dafuer `setInterval` am Aufrufer, nicht dieser Hook
 *   - Eine Flaeche, die ohnehin pollt: sie holt sich die Erholung selbst
 *
 * @related
 *   - `ServiceOutageProvider` — feuert das Ereignis; ohne ihn passiert nichts
 *   - `EmptyState` — blendet den Fehlerzweig im Ausfall aus und holt ihn nach der Erholung zurueck
 *   - `ServiceOutageBanner` — die sichtbare Aussage desselben Zustands
 *
 * @reuseHints
 *   - Die uebrigen Flaechen mit einem einmaligen Abruf beim Einhaengen
 *
 * @param {() => void} reload — der BESTEHENDE Ladeweg der Flaeche (kein zweiter).
 */
export default function useReloadOnRecovery(reload) {
  // Die juengste Funktion in der Referenz — der Zuhoerer haengt deshalb EINMAL
  // und liest trotzdem immer den aktuellen Stand (s. Kopf).
  const juengste = useRef(reload);
  useEffect(() => {
    juengste.current = reload;
  }, [reload]);

  useEffect(() => {
    const beiErholung = () => {
      // `?.` weil ein Aufrufer `undefined` uebergeben darf, ohne dass der
      // Zuhoerer daran stirbt — er ist dann einfach wirkungslos.
      juengste.current?.();
    };
    window.addEventListener(ERHOLUNGS_EREIGNIS, beiErholung);
    return () => window.removeEventListener(ERHOLUNGS_EREIGNIS, beiErholung);
  }, []);
}
